import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { BodyText, CheckBox, Flex, RadioButton } from '@kvdbil/components';
import styled from 'styled-components';
import { FormData, FormField } from './DynamicForm';
import { HelperText } from './styledComponents';
import { getOtherKey, getOtherLabel } from './helpers';
import TextFieldWithControl from '~/App/shared/components/Fields/TextFieldWithControl';
import { useTranslation } from '~/Locale';

const AnswersWrapper = styled(Flex)`
  padding-left: 1rem;
`;

type Props = {
  field: FormField;
  control: Control<FormData>;
};

const MultipleChoiceField = ({ field, control }: Props) => {
  const { t } = useTranslation();
  const {
    backendKey,
    helperText,
    multipleChoiceLabel,
    choices,
    choiceFieldType,
    required
  } = field;
  const isMultipleSelection = choiceFieldType === 'checkbox';
  const ChoiceComponent = isMultipleSelection ? CheckBox : RadioButton;
  const otherLabel = getOtherLabel(field);
  const choiceLabels = [
    ...(choices?.map(({ label }) => label) ?? []),
    ...(otherLabel ? [otherLabel] : [])
  ];

  return (
    <Flex direction="column" gap={0.5}>
      <BodyText>{multipleChoiceLabel}</BodyText>
      <Controller
        name={backendKey}
        control={control}
        rules={{
          validate: value => {
            if (required) {
              return Array.isArray(value) && value.length > 0;
            }
            return true;
          }
        }}
        render={({ field: controllerField, fieldState }) => {
          const selectedValues = Array.isArray(controllerField.value)
            ? controllerField.value
            : [];

          return (
            <AnswersWrapper direction="column" gap={0.5}>
              {choiceLabels.map((label, index) => {
                if (!label) return null;

                return (
                  <ChoiceComponent
                    key={`answer_${index}_${label}`}
                    name={`${backendKey}-choice-${index}`}
                    value={label}
                    label={label}
                    checked={selectedValues.includes(label)}
                    color="info"
                    onChange={e => {
                      const newValue = e.target.value;
                      if (e.target.checked) {
                        controllerField.onChange([
                          ...(isMultipleSelection ? selectedValues : []),
                          newValue
                        ]);
                      } else {
                        controllerField.onChange(
                          isMultipleSelection
                            ? selectedValues.filter(val => val !== newValue)
                            : []
                        );
                      }
                    }}
                    hasError={Boolean(fieldState.error)}
                  />
                );
              })}
              {otherLabel && selectedValues.includes(otherLabel) && (
                <TextFieldWithControl
                  control={control}
                  name={getOtherKey(backendKey)}
                  maxLength={256}
                  required
                  rules={{
                    required: {
                      value: true,
                      message: t(`This field can't be blank`)
                    }
                  }}
                />
              )}
            </AnswersWrapper>
          );
        }}
      />
      {helperText && <HelperText as="span">{helperText}</HelperText>}
    </Flex>
  );
};

export default MultipleChoiceField;
